/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { Assistant, Conversation, FileUpload, CompletionMessage } from '@app-libs/model';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ConfigurationClientService } from '../client/configuration-client.service';
import { ChatType } from './core/chat.types';
import { ChatState, IChatStateService } from './interfaces/chat-state-service.interface';

/**
 * ChatStateService - Cleaner API with less boilerplate
 *
 * Key improvements:
 * - Direct property setters for common operations
 * - Automatic change detection and immutability
 * - Reduced boilerplate with dynamic property access
 * - Maintains reactive patterns with less complexity
 */
@Injectable({
  providedIn: 'root',
})
export class ChatStateService implements IChatStateService {
  private readonly MAX_MESSAGES = 1000; // Maximum messages to keep in memory
  private readonly DEFAULT_KEEP_MESSAGES = 10; // Default sliding window size

  private readonly stateSubject = new BehaviorSubject<ChatState>({
    workspaceId: '',
    messages: [],
    userMessage: '',
    currentBotMessage: '',
    uploadedFile: undefined,
    selectedModel: '',
    preferredModel: null,
    keepMessages: this.DEFAULT_KEEP_MESSAGES,
    chatType: ChatType.Chat,
    conversation: {},
    assistant: {},
    threadId: undefined,
    processing: false,
    modelSelectorEnabled: false,
    enableAutoScrolling: true,
    isStreaming: false,
  });

  // Single public observable for entire state
  public readonly state$ = this.stateSubject.asObservable();

  // Dynamic property observables - created on demand
  private propertyObservables = new Map<keyof ChatState, Observable<any>>();

  constructor(private config: ConfigurationClientService) {
    this.selectedModel = this.config.defaultModel;
  }

  /**
   * Get observable for a specific property
   * Caches observables to avoid recreating them
   */
  public select<K extends keyof ChatState>(key: K): Observable<ChatState[K]> {
    if (!this.propertyObservables.has(key)) {
      this.propertyObservables.set(
        key,
        this.state$.pipe(
          map((state) => state[key]),
          distinctUntilChanged(),
        ),
      );
    }
    return this.propertyObservables.get(key)!;
  }

  /**
   * Generic property setter - works for any state property
   * Automatically handles immutability
   */
  private set<K extends keyof ChatState>(key: K, value: ChatState[K]): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      [key]: value,
    });
  }

  /**
   * Batch update multiple properties at once
   */
  public update(updates: Partial<ChatState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      ...updates,
    });
  }

  /**
   * Advanced update for complex state mutations
   * Use only when simple setters aren't sufficient
   */
  public updateState(updater: (state: ChatState) => ChatState): void {
    const currentState = this.stateSubject.value;
    const newState = updater(currentState);
    this.stateSubject.next(newState);
  }

  // ===== Simple Property Setters =====
  // These provide a clean API for common operations

  set workspaceId(value: string) {
    this.set('workspaceId', value);
  }
  get workspaceId(): string {
    return this.stateSubject.value.workspaceId;
  }

  set userMessage(value: string) {
    this.set('userMessage', value);
  }
  get userMessage(): string {
    return this.stateSubject.value.userMessage;
  }

  set currentBotMessage(value: string) {
    this.set('currentBotMessage', value);
  }
  get currentBotMessage(): string {
    return this.stateSubject.value.currentBotMessage;
  }

  set uploadedFile(value: FileUpload | undefined) {
    this.set('uploadedFile', value);
  }
  get uploadedFile(): FileUpload | undefined {
    return this.stateSubject.value.uploadedFile;
  }

  set selectedModel(value: string) {
    this.set('selectedModel', value);
  }
  get selectedModel(): string {
    return this.stateSubject.value.selectedModel;
  }

  set preferredModel(value: string | null) {
    this.set('preferredModel', value);
  }
  get preferredModel(): string | null {
    return this.stateSubject.value.preferredModel;
  }

  set keepMessages(value: number) {
    this.set('keepMessages', value);
  }
  get keepMessages(): number {
    return this.stateSubject.value.keepMessages;
  }

  set chatType(value: ChatType) {
    this.set('chatType', value);
  }
  get chatType(): ChatType {
    return this.stateSubject.value.chatType;
  }

  set conversation(value: Partial<Conversation>) {
    this.set('conversation', value);
  }
  get conversation(): Partial<Conversation> {
    return this.stateSubject.value.conversation;
  }

  set assistant(value: Partial<Assistant>) {
    this.set('assistant', value);
  }
  get assistant(): Partial<Assistant> {
    return this.stateSubject.value.assistant;
  }

  set threadId(value: string | undefined) {
    this.set('threadId', value);
  }
  get threadId(): string | undefined {
    return this.stateSubject.value.threadId;
  }

  set processing(value: boolean) {
    this.set('processing', value);
  }
  get processing(): boolean {
    return this.stateSubject.value.processing;
  }

  set modelSelectorEnabled(value: boolean) {
    this.set('modelSelectorEnabled', value);
  }
  get modelSelectorEnabled(): boolean {
    return this.stateSubject.value.modelSelectorEnabled;
  }

  set enableAutoScrolling(value: boolean) {
    this.set('enableAutoScrolling', value);
  }
  get enableAutoScrolling(): boolean {
    return this.stateSubject.value.enableAutoScrolling;
  }
  set isStreaming(value: boolean) {
    this.set('isStreaming', value);
  }
  get isStreaming(): boolean {
    return this.stateSubject.value.isStreaming;
  }

  // ===== Message Operations =====
  // Special handling for array operations

  get messages(): Partial<CompletionMessage>[] {
    return this.stateSubject.value.messages;
  }

  set messages(value: Partial<CompletionMessage>[]) {
    this.set('messages', value);
  }

  addMessage(message: Partial<CompletionMessage>): void {
    const newMessages = [...this.messages, message];

    // Enforce maximum message limit to prevent memory leaks
    if (newMessages.length > this.MAX_MESSAGES) {
      // Keep the most recent messages within the limit
      this.messages = newMessages.slice(-this.MAX_MESSAGES);
    } else {
      this.messages = newMessages;
    }
  }

  updateMessage(index: number, updates: Partial<CompletionMessage>): void {
    const messages = [...this.messages];
    if (index >= 0 && index < messages.length) {
      messages[index] = { ...messages[index], ...updates };
      this.messages = messages;
    }
  }

  updateMessageById(messageId: string, updates: Partial<CompletionMessage>): void {
    this.messages = this.messages.map((msg) =>
      (msg as any).message_id === messageId ? { ...msg, ...updates } : msg,
    );
  }

  updateMessageByIndex(index: number, updates: Partial<CompletionMessage>): void {
    this.updateMessage(index, updates);
  }

  updateStreamingMessage(index: number, content: string): void {
    this.updateMessage(index, { content });
  }

  removeMessage(messageId: string): void {
    this.messages = this.messages.filter((msg) => (msg as any).message_id !== messageId);
  }

  appendToBotMessage(chunk: string): void {
    this.currentBotMessage = this.currentBotMessage + chunk;
  }

  // ===== Convenience Methods =====

  setProcessingAndMessage(processing: boolean, userMessage = ''): void {
    this.update({ processing, userMessage });
  }

  initConversation(conversation: Partial<Conversation>, messages: Partial<CompletionMessage>[]): void {
    this.update({ conversation, messages });
  }

  configureAssistant(assistant: Partial<Assistant>, selectedModel?: string): void {
    const updates: Partial<ChatState> = { assistant };
    if (selectedModel) {
      updates.selectedModel = selectedModel;
    }
    this.update(updates);
  }

  // ===== State Access =====

  get currentState(): ChatState {
    return this.stateSubject.value;
  }

  /**
   * Clear old messages to maintain sliding window behavior
   * Called when messages exceed the keepMessages limit
   */
  private clearOldMessages(): void {
    const currentMessages = this.messages;
    const keepCount = this.keepMessages;

    if (currentMessages.length > keepCount) {
      // Keep only the most recent messages
      this.messages = currentMessages.slice(-keepCount);
    }
  }

  /**
   * Manually trigger message cleanup
   * Useful for testing or manual memory management
   */
  public trimMessages(count?: number): void {
    const trimCount = count || this.keepMessages;
    const currentMessages = this.messages;

    if (currentMessages.length > trimCount) {
      this.messages = currentMessages.slice(-trimCount);
    }
  }

  // ===== State Reset =====

  resetState(): void {
    this.stateSubject.next({
      workspaceId: this.workspaceId, // Preserve workspace
      messages: [],
      userMessage: '',
      currentBotMessage: '',
      uploadedFile: undefined,
      selectedModel: this.config.defaultModel,
      preferredModel: null,
      keepMessages: this.DEFAULT_KEEP_MESSAGES,
      chatType: ChatType.Chat,
      conversation: {},
      assistant: {},
      threadId: undefined,
      processing: false,
      modelSelectorEnabled: false,
      enableAutoScrolling: true,
      isStreaming: false,
    });
  }

  // ===== Computed Properties =====

  get hasMessages(): boolean {
    return this.messages.length > 0;
  }

  get canSendMessage(): boolean {
    return this.userMessage.trim().length > 0 && !this.processing;
  }

  get isConversationActive(): boolean {
    return !!(this.conversation as any).conversation_id;
  }

  get messageCount(): number {
    return this.messages.length;
  }

  get lastMessage(): Partial<CompletionMessage> | null {
    const messages = this.messages;
    return messages.length > 0 ? messages[messages.length - 1] : null;
  }

  // ===== Observable Shortcuts =====
  // For commonly used observables (implementing IChatStateService)

  get messages$(): Observable<Partial<CompletionMessage>[]> {
    return this.select('messages');
  }
  get processing$(): Observable<boolean> {
    return this.select('processing');
  }
  get selectedModel$(): Observable<string> {
    return this.select('selectedModel');
  }
  get preferredModel$(): Observable<string | null> {
    return this.select('preferredModel');
  }
  get workspaceId$(): Observable<string> {
    return this.select('workspaceId');
  }
  get conversation$(): Observable<Partial<Conversation>> {
    return this.select('conversation');
  }
  get assistant$(): Observable<Partial<Assistant>> {
    return this.select('assistant');
  }
  get uploadedFile$(): Observable<FileUpload | undefined> {
    return this.select('uploadedFile');
  }
  get chatType$(): Observable<ChatType> {
    return this.select('chatType');
  }
  get modelSelectorEnabled$(): Observable<boolean> {
    return this.select('modelSelectorEnabled');
  }
  get enableAutoScrolling$(): Observable<boolean> {
    return this.select('enableAutoScrolling');
  }
  get isStreaming$(): Observable<boolean> {
    return this.select('isStreaming');
  }
  get userMessage$(): Observable<string> {
    return this.select('userMessage');
  }
  get currentBotMessage$(): Observable<string> {
    return this.select('currentBotMessage');
  }
  get keepMessages$(): Observable<number> {
    return this.select('keepMessages');
  }
  get threadId$(): Observable<string | undefined> {
    return this.select('threadId');
  }

  // Additional computed observables
  get hasMessages$(): Observable<boolean> {
    return this.messages$.pipe(
      map((messages) => messages.length > 0),
      distinctUntilChanged(),
    );
  }

  get canSendMessage$(): Observable<boolean> {
    return this.state$.pipe(
      map((state) => state.userMessage.trim().length > 0 && !state.processing),
      distinctUntilChanged(),
    );
  }

  get isConversationActive$(): Observable<boolean> {
    return this.conversation$.pipe(
      map((conversation) => !!(conversation as any).conversation_id),
      distinctUntilChanged(),
    );
  }

  get messageCount$(): Observable<number> {
    return this.messages$.pipe(
      map((messages) => messages.length),
      distinctUntilChanged(),
    );
  }

  get lastMessage$(): Observable<Partial<CompletionMessage> | null> {
    return this.messages$.pipe(
      map((messages) => (messages.length > 0 ? messages[messages.length - 1] : null)),
      distinctUntilChanged(),
    );
  }
}
